import { Bell } from 'lucide-react'
import { NavLink } from 'react-router-dom'

import {
  RAIL_BUTTON_ACTIVE_CLASS,
  RAIL_BUTTON_CLASS,
  SidebarMenuButton,
} from '@/components/ui/sidebar'
import { NOTIFICATIONS_BADGE_MAX } from '@/config/constants'
import { useUnreadCountQuery } from '@/features/notifications/hooks/useUnreadCountQuery'
import { cn } from '@/lib/utils'

interface NotificationBellProps {
  /** Menu-variant label visibility. Unused by the rail variant. */
  open?: boolean
  /** 'menu' (default) is today's list-item markup; 'rail' is the icon rail. */
  variant?: 'menu' | 'rail'
}

/**
 * Sidebar entry for the notifications view, with an unread badge.
 *
 * A link rather than a popover: the inbox is a full view now, so the rail's job
 * is to open it and to signal that something is waiting. The rail variant shows
 * a dot rather than a number, per the design; the count rides in the link's
 * accessible name, and the menu variant the mobile drawer uses keeps the
 * numeric badge. The badge clears on read, not on
 * navigation, so this fires no mutation. A failed count renders no badge and no
 * toast — when relay lags a hoku deploy this request fails on every poll, and
 * the bell degrading quietly beats a toast storm.
 */
export function NotificationBell({
  open,
  variant = 'menu',
}: NotificationBellProps) {
  const { data } = useUnreadCountQuery()
  const unread = data?.unread ?? 0
  const badge =
    unread > NOTIFICATIONS_BADGE_MAX ? `${NOTIFICATIONS_BADGE_MAX}+` : unread
  // The accessible name carries the count in both variants, so the
  // announcement behaviour is the same regardless of which markup renders.
  const accessibleName = `Notifications${unread > 0 ? ` (${badge} unread)` : ''}`

  if (variant === 'rail') {
    return (
      <NavLink
        to="/notifications"
        aria-label={accessibleName}
        // Lights on its own page like the rail's category buttons do. The
        // old container-level `.active` rule died with the expanding panel.
        className={({ isActive }) =>
          cn(RAIL_BUTTON_CLASS, isActive && RAIL_BUTTON_ACTIVE_CLASS)
        }>
        <span className="relative flex items-center justify-center">
          <Bell size={22} strokeWidth={1.2} />
          {unread > 0 && (
            <span
              aria-hidden
              className="absolute -right-0.5 -top-0.5 size-2.5 rounded-full bg-error-solid ring-2 ring-white"
            />
          )}
        </span>
      </NavLink>
    )
  }

  return (
    <SidebarMenuButton asChild>
      <NavLink to="/notifications">
        <span className="relative flex size-4 items-center justify-center">
          <Bell className="size-4" />
          {unread > 0 && (
            <span
              aria-hidden
              className={cn(
                'absolute -right-1.5 -top-1.5 flex min-w-[15px] items-center',
                'justify-center rounded-full bg-primary px-[3px] text-[9px]',
                'font-semibold leading-[15px] text-primary-foreground'
              )}>
              {badge}
            </span>
          )}
        </span>
        <span className={cn('text-xs', open ? undefined : 'sr-only')}>
          {accessibleName}
        </span>
      </NavLink>
    </SidebarMenuButton>
  )
}
